import api from './api'
import {
  CompetenciaDetalhe,
  CompetenciasComissaoResponse,
  FechamentoCompetencia,
  MesCompetencia,
  PendenciasFechamento,
} from '@/types/competencia'

/**
 * Competência fechada da comissão (AN-PP5 §4, §5) — `CompetenciaComissaoController`.
 *
 * Leituras: back-office (OPERADOR/GERENTE/ADMIN) ou FINANCEIRO; a persona consultor nunca (403
 * `COMISSAO_SO_BACKOFFICE`). Fechar: FINANCEIRO ou ADMIN; reabrir: só ADMIN — os dois sem escopo de
 * tenant (403 `COMPETENCIA_SO_GLOBAL`). Competência na URL em `aaaa-mm`.
 */
const BASE_URL = '/api/v1/comissoes/competencias'

const mes = (competencia: MesCompetencia) => encodeURIComponent(competencia)

export const competenciaService = {
  /** `de`/`ate` em `aaaa-mm`; sem eles, os 12 meses até o corrente (backend). */
  listar: async (de?: MesCompetencia, ate?: MesCompetencia): Promise<CompetenciasComissaoResponse> => {
    const params = new URLSearchParams()
    if (de) params.set('de', de)
    if (ate) params.set('ate', ate)
    const qs = params.toString()
    return await api.get<CompetenciasComissaoResponse>(`${BASE_URL}${qs ? `?${qs}` : ''}`)
  },

  /** Histórico de fechamentos/reaberturas com o snapshot (itens e ajustes). */
  detalhar: async (competencia: MesCompetencia): Promise<CompetenciaDetalhe> => {
    return await api.get<CompetenciaDetalhe>(`${BASE_URL}/${mes(competencia)}`)
  },

  /** PROJETADOS que impedem o fechamento (no primeiro fechamento, todo o passado até ela). */
  pendencias: async (competencia: MesCompetencia): Promise<PendenciasFechamento> => {
    return await api.get<PendenciasFechamento>(`${BASE_URL}/${mes(competencia)}/pendencias-fechamento`)
  },

  /**
   * Sem corpo. Erros: 422 `COMPETENCIA_EM_CURSO`; 409 `COMPETENCIA_JA_FECHADA`,
   * `COMPETENCIA_FORA_DE_SEQUENCIA` (`data.primeiraAberta`), `COMPETENCIA_COM_PROJETADAS`
   * (`data.quantidade`, `data.competencias`), `FECHAMENTO_CONCORRENTE`; 403 `COMPETENCIA_SO_GLOBAL`.
   */
  fechar: async (competencia: MesCompetencia): Promise<FechamentoCompetencia> => {
    return await api.post<FechamentoCompetencia>(`${BASE_URL}/${mes(competencia)}/fechar`)
  },

  /** Só ADMIN. Motivo de 10 a 1000 caracteres (422 `MOTIVO_REABERTURA_INVALIDO`); 409 `COMPETENCIA_NAO_REABRIVEL`. */
  reabrir: async (competencia: MesCompetencia, motivo: string): Promise<FechamentoCompetencia> => {
    return await api.post<FechamentoCompetencia>(`${BASE_URL}/${mes(competencia)}/reabrir`, { motivo })
  },
}

export default competenciaService
